import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Users, Coins, UserCheck, UserX, Calendar, AlertCircle, FlaskConical } from 'lucide-react';
import DashboardCard from '@/components/ui/DashboardCard';
import { dashboardService } from '@/services/dashboardService';
import { AgentDashboardData } from '@/types';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { useAuth, usePurchaseDemoCredits } from '@/hooks';
import toast from 'react-hot-toast';

const getSaleCreditsCardVariant = (saleCredits: number) => {
  if (saleCredits < 12) return 'danger';
  if (saleCredits === 12) return 'warning';

  return 'success';
};

const AgentDashboard: React.FC = () => {
  const [data, setData] = useState<AgentDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemoInfoModalOpen, setIsDemoInfoModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user: currentUser, refreshAuth } = useAuth();
  const purchaseDemoCreditsMutation = usePurchaseDemoCredits();
  const demoCreditsAvailable = currentUser?.demoCreditsAvailable ?? data?.demoCreditsAvailable ?? 0;

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const dashboardData = await dashboardService.getAgentDashboard();
      setData(dashboardData);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleExpiredUsersClick = () => {
    navigate('/users?status=expired');
  };

  const handleUsersAboutToExpireClick = () => {
    navigate('/users?status=aboutToExpire');
  };

  const handleRegisteredUsersClick = () => {
    navigate('/users');
  };

  const handleDemoCreditsClick = () => {
    setIsDemoInfoModalOpen(true);
  };

  const handlePurchaseDemoCredits = async () => {
    try {
      const response = await purchaseDemoCreditsMutation.mutateAsync();
      await refreshAuth();
      await loadDashboardData();
      toast.success(response.message || 'Créditos demo comprados correctamente');
      setIsDemoInfoModalOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudieron comprar los créditos demo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Agente</h1>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Agente</h1>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const showRegisteredAgents = currentUser?.roleCode === 'superagent';
  const saleCreditsCardVariant = getSaleCreditsCardVariant(data.totalSaleCredits);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Agente</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <DashboardCard
            title="Créditos de Venta Disponibles"
            value={data.totalSaleCredits.toLocaleString()}
            icon={<Coins className="h-8 w-8" />}
            variant={saleCreditsCardVariant}
          />

          <DashboardCard
            title="Usuarios Vencidos"
            value={data.expiredUsers.toLocaleString()}
            subtitle="Haz clic para ver la lista"
            icon={<UserX className="h-8 w-8" />}
            onClick={handleExpiredUsersClick}
            isClickable={true}
            variant="danger"
          />

          <DashboardCard
            title="Usuarios por Vencer"
            value={data.usersAboutToExpire.toLocaleString()}
            subtitle="En el mes actual"
            icon={<Calendar className="h-8 w-8" />}
            onClick={handleUsersAboutToExpireClick}
            isClickable={true}
            variant="warning"
          />

          <DashboardCard
            title="Usuarios Registrados"
            value={data.totalRegisteredUsers.toLocaleString()}
            subtitle="Por este agente"
            onClick={handleRegisteredUsersClick}
            isClickable={true}
            icon={<Users className="h-8 w-8" />}
          />

          {showRegisteredAgents && (
            <DashboardCard
              title="Agentes Registrados"
              value={data.totalRegisteredAgents.toLocaleString()}
              subtitle="Por este agente"
              icon={<UserCheck className="h-8 w-8" />}
            />
          )}

          <DashboardCard
            title="Créditos Demo Disponibles"
            value={demoCreditsAvailable.toLocaleString()}
            subtitle="Haz clic para ver cómo funciona"
            icon={<FlaskConical className="h-8 w-8" />}
            onClick={handleDemoCreditsClick}
            isClickable={true}
          />
        </div>
      </div>

      <Modal
        isOpen={isDemoInfoModalOpen}
        onClose={() => setIsDemoInfoModalOpen(false)}
        title="Créditos Demo"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Créditos demo disponibles: <span className="font-semibold text-gray-900">{demoCreditsAvailable}</span>
            </p>
          </div>

          <div className="text-sm text-gray-700 space-y-2">
            <p>1. Cada vendedor recibe 50 créditos demo para otorgar demos de 1 día.</p>
            <p>2. Cada demo otorgado consume 1 crédito demo.</p>
            <p>3. Cuando llegas a 0, puedes comprar 30 créditos demo usando 1 crédito de venta.</p>
          </div>

          {demoCreditsAvailable <= 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                No tienes créditos demo disponibles. Puedes comprarlos ahora.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setIsDemoInfoModalOpen(false)}
              disabled={purchaseDemoCreditsMutation.isPending}
            >
              Cerrar
            </Button>
            {demoCreditsAvailable <= 0 && (
              <Button
                onClick={handlePurchaseDemoCredits}
                loading={purchaseDemoCreditsMutation.isPending}
                disabled={purchaseDemoCreditsMutation.isPending}
              >
                Comprar 30 créditos demo
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentDashboard;
